import { useContext, useEffect, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import CardFinalExamination from "./ui"
import { ICardFinalExamination, ICardFinalExaminationContainer, IFieldsError } from "./types"

import { useValue } from "@hooks/useValue"
import { useFinalExamination } from "@api/finalExamination/state"

import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"
import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


type FinalExaminationFormState = {
    themeIAR: string
    iarScore: string
    testScore: string
}

const createFinalExaminationFormSnapshot = (state: FinalExaminationFormState) => JSON.stringify(state)

/** Контейнер анкеты итоговой работы */
export const CardFinalExaminationContainer = ({isOpen, cardID, fullNameFrom, attestationType, dataBase, handlerButtonOk, handlerButtonCancel}: ICardFinalExaminationContainer) => {
    const notify = useContext(NotificationContext)

    const {isLoadingCard, getCardData, saveCardData, sendInArchive, sendToPrint} = useFinalExamination(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        getCardData: state.actionGetFinalExaminationCard,
        saveCardData: state.actionSetFinalExaminationCardSave,
        sendInArchive: state.actionSetFinalExaminationInArchive,
        sendToPrint: state.actionSetFinalExaminationToPrint,
    })))

    const modalConfirm = useValue<IModalConfirm>({isOpen: false})

    const loadingTitle = useValue<string>('Загрузка анкеты...')
    const fieldsError = useValue<IFieldsError>({})

    const txtThemeIAR = useValue<string>('')
    const txtBallIAR = useValue<string>('')
    const txtBallTest = useValue<string>('')
    const isDisableBtnSave = useValue<boolean>(true)
    const savedFormSnapshotRef = useRef(createFinalExaminationFormSnapshot({themeIAR: '', iarScore: '', testScore: ''}))
    const hasSavedChangesRef = useRef(false)
    const isFormReadyRef = useRef(false)
    
    const isBtnToPrint = useValue<number>(0)

    const getCurrentFormState = (): FinalExaminationFormState => {
        const isIT = attestationType === 'ИТ'
        return {
            themeIAR: isIT ? '' : txtThemeIAR.value,
            iarScore: isIT ? '' : txtBallIAR.value,
            testScore: isIT ? txtBallTest.value : '',
        }
    }
    const applyCardData = (card: {themeIAR: string; iarScore: string; testScore: string; sendPrinting: number}) => {
        txtThemeIAR.setValue(card.themeIAR || '')
        txtBallIAR.setValue(card.iarScore || '')
        txtBallTest.setValue(card.testScore || '')
        isBtnToPrint.setValue(card.sendPrinting || 0)

        const isIT = attestationType === 'ИТ'
        savedFormSnapshotRef.current = createFinalExaminationFormSnapshot({
            themeIAR: isIT ? '' : card.themeIAR || '',
            iarScore: isIT ? '' : card.iarScore || '',
            testScore: isIT ? card.testScore || '' : '',
        })
        isFormReadyRef.current = true
        isDisableBtnSave.setValue(true)
    }

    useEffect(() => {
        let isCurrentRequest = true

        if (isOpen) {
            clearCard()
            getCardData(Number(cardID)).then((res) => {
                if (!isCurrentRequest) return
                if (res) applyCardData(res)
            })
        }

        !isOpen && clearCard() // Обнуление данных по анкете

        return () => {
            isCurrentRequest = false
        }
    }, [isOpen, cardID]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = createFinalExaminationFormSnapshot(getCurrentFormState())
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    /** Функция обнуления данных по карточке */
    const clearCard = () => {
        fieldsError.setValue({})
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        savedFormSnapshotRef.current = createFinalExaminationFormSnapshot({themeIAR: '', iarScore: '', testScore: ''})

        txtThemeIAR.setValue('')
        txtBallIAR.setValue('')
        txtBallTest.setValue('')
        
        isBtnToPrint.setValue(0)
    }

    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handlerButtonOk && handlerButtonOk()
        } else {
            handlerButtonCancel && handlerButtonCancel()
        }
    }
    const handleBtnClose = () => {
        if (isLoadingCard || modalConfirm.value.isOpen) return

        if (isDisableBtnSave.value) {
            closeCard()
            return
        }

        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Сохранение формы',
            contentText: '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?',
            cancelButtonText: 'Продолжить редактирование',
            okButtonText: 'Закрыть без сохранения',
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            handleButtonOk() {closeCard()},
        })
    }

    /** Открытие/закрытие окна подтверждения */
    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionName?: string) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText,
            handleButtonCancel() { modalConfirm.setValue({ isOpen: false }) },
            handleButtonOk() {
                switch (actionName) {
                    case 'inArchive': {
                        loadingTitle.setValue('Отправляю в архив...')
                        sendInArchive(Number(cardID)).then((res) => {
                            if (res === 200) {
                                handlerButtonOk && handlerButtonOk()
                                return notify && notifyOpen('Анкета итоговой работы слушателя, успешно отправлена в архив.', 'success', 1500, notify)
                            }
                        }).finally(() => {loadingTitle.setValue('Загрузка анкеты...')})
                        break
                    } // Создание и получение нового номера по договору

                    case 'toPrint': {
                        loadingTitle.setValue('Отправляю в печать...')

                        sendToPrint(Number(cardID)).then((res) => {
                            if (res === 200) {
                                handlerButtonOk && handlerButtonOk()
                                return notify && notifyOpen('Анкета итоговой работы слушателя, успешно отправлена в печать.', 'success', 1500, notify)
                            }
                        }).finally(() => {loadingTitle.setValue('Загрузка анкеты...')})
                        break
                    } // Допуск слушателя к обучению
                }

                modalConfirm.setValue({ isOpen: false })
            }
        })
    }
    
    /** Кнопка сохранить в анкете */
    const handlerBtnSave = (isClosedModal: boolean) => {
        const formState = getCurrentFormState()

        saveCardData(
            Number(cardID),
            formState.themeIAR.trim(),
            formState.iarScore.trim(),
            formState.testScore.trim()
        ).then((res) => {
            if (res === 200) {
                notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)
                hasSavedChangesRef.current = true

                if (isClosedModal) {
                    handlerButtonOk && handlerButtonOk()
                    return
                }

                savedFormSnapshotRef.current = createFinalExaminationFormSnapshot(formState)
                isDisableBtnSave.setValue(true)
                getCardData(Number(cardID)).then((card) => {
                    if (card) applyCardData(card)
                })
            }
        })
    }

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardFinalExamination = {
        isOpen, fullNameFrom, attestationType, dataBase,

        isLoadingCard, isDisableBtnSave, loadingTitle, isBtnToPrint,
        fieldsError, txtThemeIAR, txtBallIAR, txtBallTest,
        handlerOpenModalConfirm, handlerBtnSave, handleBtnClose,
    }

    return <>
        <CardFinalExamination {...propsToComponent} />

        <ModalConfirm {...modalConfirm.value} />
    </>
}